import { useEffect, useRef, useState } from "react";

let apiPromise = null;
function loadYtApi() {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (!apiPromise) {
    apiPromise = new Promise((resolve) => {
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { prev?.(); resolve(window.YT); };
      const s = document.createElement("script");
      s.src = "https://www.youtube.com/iframe_api";
      document.head.appendChild(s);
    });
  }
  return apiPromise;
}

// YouTube's embed shows its title bar / channel chip for ~3 s after playback starts; keep the tile covered until then.
const REVEAL_DELAY_MS = 3200;
// Slight zoom so any sliver of YouTube chrome at the top/bottom edge is cropped out of the frame.
const ZOOM = 1.12;

// Muted autoplaying YouTube player. Stays behind an opaque "Connecting" cover until the video has actually been
// playing long enough for YouTube's overlay text to fade, and reports playback errors (100/101/150) so the wall can hide the feed.
export function YtCamPlayer({ videoId, title, style, onError }) {
  const hostRef = useRef(null);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    let player = null, cancelled = false, revealTimer = null;
    setRevealed(false);
    loadYtApi().then((YT) => {
      if (cancelled || !hostRef.current) return;
      player = new YT.Player(hostRef.current, {
        videoId,
        playerVars: { autoplay: 1, mute: 1, controls: 0, modestbranding: 1, rel: 0, loop: 1, playlist: videoId, playsinline: 1, iv_load_policy: 3, disablekb: 1, fs: 0 },
        events: {
          onReady: (e) => { try { e.target.mute(); e.target.playVideo(); } catch { /* ignore */ } },
          onStateChange: (e) => {
            if (e.data === YT.PlayerState.PLAYING) {
              if (!revealTimer) revealTimer = setTimeout(() => { if (!cancelled) setRevealed(true); }, REVEAL_DELAY_MS);
            } else if (e.data === YT.PlayerState.ENDED || e.data === YT.PlayerState.PAUSED) {
              clearTimeout(revealTimer); revealTimer = null;
              setRevealed(false);
              try { e.target.playVideo(); } catch { /* ignore */ }
            }
          },
          onError: (e) => onErrorRef.current?.(e.data),
        },
      });
    });
    return () => { cancelled = true; clearTimeout(revealTimer); try { player?.destroy(); } catch { /* ignore */ } };
  }, [videoId]);
  return (
    <div className="absolute inset-0 h-full w-full overflow-hidden" style={style} data-testid={`yt-cam-${videoId}`} data-revealed={revealed ? "true" : "false"}>
      <div className="h-full w-full" style={{ transform: `scale(${ZOOM})`, transformOrigin: "center center" }}>
        <div ref={hostRef} title={title} className="h-full w-full" />
      </div>
      <div
        className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#0a0f1c] transition-opacity duration-500"
        style={{ opacity: revealed ? 0 : 1 }}
        data-testid={`yt-cam-cover-${videoId}`}
        aria-hidden
      >
        <div className="flex items-center gap-3 font-oswald text-[22px] font-black uppercase tracking-[0.35em] text-white/80">
          <span className="live-dot" style={{ width: 10, height: 10 }} /> Connecting
        </div>
        <div className="max-w-[80%] truncate font-oswald text-[16px] font-bold uppercase tracking-[0.25em] text-white/40">{title}</div>
        <div className="mt-2 h-[3px] w-[160px] overflow-hidden rounded-full bg-white/10">
          <div className="h-full w-1/3 rounded-full bg-[#d90429] cam-connect-bar" />
        </div>
      </div>
    </div>
  );
}
